import { datadogLogs } from '@datadog/browser-logs';
import { string as zString } from 'zod';

// First-party proxy for Datadog's browser-logs intake. Routing logs through our
// own subdomain makes the traffic first-party, so ad blockers and privacy
// browsers that block Datadog's intake don't drop our logs. The SDK appends a
// `?ddforward=` query param and the proxy forwards to Datadog's real intake.
//
// Each environment proxies through its own first-party subdomain.
const INTAKE_PROXY_BY_ENV: Record<string, string> = {
  staging: 'https://intake-proxy.staging.hungryroot.com',
  production: 'https://intake-proxy.hungryroot.com',
};

export function initialize() {
  const result = zString({
    required_error: 'Missing PUBLIC_DATADOG_KEY from process.env',
  }).safeParse(process.env.PUBLIC_DATADOG_KEY);

  if (!result.success) {
    return;
  }

  const proxy = INTAKE_PROXY_BY_ENV[process.env.PUBLIC_HR_ENV ?? ''];

  datadogLogs.init({
    env: process.env.PUBLIC_DD_ENV,
    service: 'web',
    clientToken: result.data,
    forwardErrorsToLogs: true,
    ...(proxy ? { proxy } : {}),
  });
}

export function getLogger() {
  if (datadogLogs.getInitConfiguration() === undefined) {
    initialize();
  }

  return datadogLogs.logger;
}

// A pure read, deliberately not forcing `initialize()` the way `getLogger`
// does: this is consulted from a Sentry event processor that fires well
// before the logger is guaranteed to exist, and a session id read should
// never be the reason the logger gets initialized early or a real call gets
// delayed. Returns `undefined` whenever the logger hasn't initialized yet,
// or the SDK hasn't assigned a session (both are normal, unremarkable
// states).
//
// Deliberately no try/catch here — the event processor in `sentry-client.ts`
// that calls this is the single catch boundary for the whole read. A local
// catch here would swallow a real throw silently, leaving that outer catch
// dead code.
export function getDatadogSessionId(): string | undefined {
  return datadogLogs.getInternalContext()?.session_id;
}
